// Builds platform-specific listing text from a scanned item.
// Pure functions — no DOM, no clipboard. The component handles copy actions.

export type Platform = "Depop" | "Grailed" | "eBay" | "Etsy" | "Poshmark" | "Vinted";

export interface ListingSource {
  title: string;
  brand: string;
  era: string;
  origin?: string;
  category?: string;
  material?: string;
  size: string;
  color?: string;
  condition: string;
  conditionNotes?: string;
  tags: string[];
  price: number;
}

export interface FormattedListing {
  title: string;
  description: string;
  tags: string;        // formatted per-platform (hashtag vs comma)
  price: string;       // "98" or "98.00" — platform dependent
  all: string;         // single block fallback for "Copy all"
  tagHelp: string;     // tiny hint shown next to the tags row
  titleNote?: string;  // e.g. "80 char max — eBay"
}

const truncate = (s: string, n: number) => (s.length <= n ? s : s.slice(0, n - 1).trimEnd() + "…");

function baseDescription(s: ListingSource): string {
  const parts: string[] = [];
  parts.push(`${s.brand} · ${s.era} · Size ${s.size}`);
  if (s.origin) parts.push(s.origin);
  parts.push("");
  if (s.material) parts.push(`Material: ${s.material}`);
  if (s.color) parts.push(`Color: ${s.color}`);
  parts.push(`Condition: ${s.condition}${s.conditionNotes ? ` — ${s.conditionNotes}` : ""}`);
  parts.push("");
  parts.push("Authenticated and dated by Fig. Certificate available on request.");
  return parts.join("\n");
}

export interface PlatformVariant {
  title?: string;
  description?: string;
}

export interface PlatformDescriptions {
  depop?: PlatformVariant;
  grailed?: PlatformVariant;
  ebay?: PlatformVariant;
  etsy?: PlatformVariant;
  poshmark?: PlatformVariant;
  vinted?: PlatformVariant;
}

function pickVariant(platform: Platform, override?: PlatformDescriptions): PlatformVariant | undefined {
  const key = platform.toLowerCase() as keyof PlatformDescriptions;
  return override?.[key];
}

function pickDescription(platform: Platform, s: ListingSource, override?: PlatformDescriptions): string {
  const ai = pickVariant(platform, override)?.description?.trim();
  return ai && ai.length > 0 ? ai : baseDescription(s);
}

function pickTitle(platform: Platform, fallback: string, max: number, override?: PlatformDescriptions): string {
  const ai = pickVariant(platform, override)?.title?.trim();
  return truncate((ai && ai.length > 0 ? ai : fallback), max);
}

export function formatForPlatform(platform: Platform, s: ListingSource, descriptions?: PlatformDescriptions): FormattedListing {
  const desc = pickDescription(platform, s, descriptions);

  switch (platform) {
    case "Depop": {
      const title = pickTitle("Depop", s.title, 65, descriptions);
      const tags = s.tags.slice(0, 5).map((t) => `#${t.replace(/\s+/g, "")}`).join(" ");
      return {
        title,
        description: desc,
        tags,
        price: String(s.price),
        all: `${title}\n\n${desc}\n\n${tags}\n\n$${s.price}`,
        tagHelp: "Hashtag style · max 5 on Depop",
      };
    }
    case "Grailed": {
      const title = pickTitle("Grailed", `${s.brand} ${s.era} ${s.title}`, 60, descriptions);
      const tags = s.tags.slice(0, 8).join(", ");
      return {
        title,
        description: desc,
        tags,
        price: String(s.price),
        all: `${title}\n\n${desc}\n\nTags: ${tags}\n\n$${s.price}`,
        tagHelp: "Comma separated — paste into the tags field",
      };
    }
    case "eBay": {
      const titleRaw = `${s.era} ${s.brand} ${s.title} Size ${s.size}`;
      const title = pickTitle("eBay", titleRaw, 80, descriptions);
      const tags = s.tags.slice(0, 10).join(", ");
      return {
        title,
        description: desc,
        tags,
        price: s.price.toFixed(2),
        all: `${title}\n\n${desc}\n\nKeywords: ${tags}\n\n$${s.price.toFixed(2)}`,
        tagHelp: "Use as item specifics / keywords",
        titleNote: "80 character max",
      };
    }
    case "Etsy": {
      const title = pickTitle("Etsy", `Vintage ${s.brand} ${s.title} ${s.era} Size ${s.size}`, 140, descriptions);
      const tags = s.tags.slice(0, 13).join(", ");
      return {
        title,
        description: desc,
        tags,
        price: s.price.toFixed(2),
        all: `${title}\n\n${desc}\n\nTags: ${tags}\n\n$${s.price.toFixed(2)}`,
        tagHelp: "Comma separated · max 13 on Etsy",
      };
    }
    case "Poshmark": {
      const title = pickTitle("Poshmark", `${s.brand} ${s.title} Size ${s.size}`, 80, descriptions);
      const tags = s.tags.slice(0, 8).join(", ");
      return {
        title,
        description: desc,
        tags,
        price: String(s.price),
        all: `${title}\n\n${desc}\n\nStyle Tags: ${tags}\n\n$${s.price}`,
        tagHelp: "Comma separated · use as style tags",
        titleNote: "80 character max",
      };
    }
    case "Vinted": {
      // Vinted titles are short and brand-led; tags ("hashtags" in their UI)
      // are a small comma-separated list. Description should be plain and
      // friendly — Vinted skews casual / EU-second-hand.
      const title = pickTitle("Vinted", `${s.brand} ${s.title} Size ${s.size}`, 50, descriptions);
      const tags = s.tags.slice(0, 5).join(", ");
      return {
        title,
        description: desc,
        tags,
        price: String(s.price),
        all: `${title}\n\n${desc}\n\nTags: ${tags}\n\n€${s.price}`,
        tagHelp: "Comma separated · keep it short on Vinted",
        titleNote: "50 character max",
      };
    }
  }
}

// Build a richer, save-for-later .txt file. Includes everything the user
// might want when they come back days later: title, description, tags, price,
// condition notes, and a "paste into platform" cheat sheet.
export function buildTxtFile(platform: Platform, s: ListingSource, formatted: FormattedListing): string {
  const line = "─".repeat(48);
  const stamp = new Date().toLocaleString();
  return [
    `FIG · LISTING DRAFT`,
    `Prepared for: ${platform}`,
    `Saved: ${stamp}`,
    line,
    ``,
    `TITLE`,
    formatted.title,
    formatted.titleNote ? `(${formatted.titleNote})` : ``,
    ``,
    `PRICE`,
    `$${formatted.price}`,
    ``,
    `DESCRIPTION`,
    formatted.description,
    ``,
    `TAGS  (${formatted.tagHelp})`,
    formatted.tags,
    ``,
    line,
    `ITEM DETAILS`,
    `Brand:     ${s.brand}`,
    `Era:       ${s.era}`,
    s.origin    ? `Origin:    ${s.origin}`    : ``,
    s.category  ? `Category:  ${s.category}`  : ``,
    s.material  ? `Material:  ${s.material}`  : ``,
    `Size:      ${s.size}`,
    s.color     ? `Color:     ${s.color}`     : ``,
    `Condition: ${s.condition}`,
    s.conditionNotes ? `Notes:     ${s.conditionNotes}` : ``,
    ``,
    line,
    `Paste each section into the matching field on ${platform}.`,
    `Photos must be added from your camera roll on ${platform}'s side.`,
  ].filter((l) => l !== ``  || true).join("\n").replace(/\n{3,}/g, "\n\n");
}

export function downloadTxt(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has had a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Deep-link / web fallback per platform.
export function platformLink(platform: Platform): { app: string; web: string; label: string } {
  switch (platform) {
    case "Depop":   return { app: "depop://sell",                   web: "https://www.depop.com/sell/",        label: "Open Depop" };
    case "Grailed": return { app: "https://www.grailed.com/sell",   web: "https://www.grailed.com/sell",       label: "Open Grailed" };
    case "eBay":    return { app: "https://www.ebay.com/sl/sell",   web: "https://www.ebay.com/sl/sell",       label: "Open eBay" };
    case "Etsy":    return { app: "https://www.etsy.com/your/shops/me/tools/listings/create", web: "https://www.etsy.com/your/shops/me/tools/listings/create", label: "Open Etsy" };
    case "Poshmark": return { app: "poshmark://sell",                web: "https://poshmark.com/create-listing", label: "Open Poshmark" };
    case "Vinted":   return { app: "vinted://sell",                  web: "https://www.vinted.com/items/new",    label: "Open Vinted" };
  }
}
